import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { staffApi } from '@/services/api'
import type { StaffMember } from '@/types'
import { Users, Plus, Trash2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

export default function StaffManagement() {
  const { currentFirm, user } = useAuth()
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' })
  const [inviting, setInviting] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState<StaffMember | null>(null)
  const [removing, setRemoving] = useState(false)

  const currentMember = staff.find((m) => m.user_id === user?.id)
  const canManage = currentMember?.role === 'admin' || currentMember?.role === 'owner'

  useEffect(() => {
    if (!currentFirm) return
    loadStaff()
  }, [currentFirm?.id])

  async function loadStaff() {
    if (!currentFirm) return
    setLoading(true)
    try {
      const res = await staffApi.list(currentFirm.id)
      setStaff(res.data.staff)
    } finally {
      setLoading(false)
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!currentFirm) return
    setInviting(true)
    try {
      await staffApi.invite(currentFirm.id, inviteForm)
      setInviteForm({ email: '', role: 'member' })
      setShowInvite(false)
      loadStaff()
      toast.success(`Invite sent to ${inviteForm.email}`)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to invite'
      toast.error(msg)
    } finally {
      setInviting(false)
    }
  }

  async function handleRoleChange(member: StaffMember, role: string) {
    if (!currentFirm) return
    try {
      await staffApi.updateRole(currentFirm.id, member.membership_id, role)
      toast.success('Role updated')
      loadStaff()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to update role'
      toast.error(msg)
    }
  }

  async function handleConfirmRemove() {
    if (!currentFirm || !confirmRemove) return
    setRemoving(true)
    try {
      const isPending = confirmRemove.status === 'invited'
      if (isPending) {
        await staffApi.cancelInvite(currentFirm.id, confirmRemove.membership_id)
        toast.success('Invite cancelled')
      } else {
        await staffApi.remove(currentFirm.id, confirmRemove.membership_id)
        toast.success('Staff member removed')
      }
      setConfirmRemove(null)
      loadStaff()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to remove'
      toast.error(msg)
    } finally {
      setRemoving(false)
    }
  }

  const isPendingInvite = (m: StaffMember) => m.status === 'invited'

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Staff</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Manage who has access to your firm</p>
        </div>
        {canManage && (
          <Button variant="create" onClick={() => setShowInvite(true)}>
            <Plus className="w-4 h-4" /> Add Staff
          </Button>
        )}
      </div>

      {/* Invite Dialog — only mounted for admin/owner */}
      {canManage && (
        <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Staff Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="colleague@example.com"
                  required
                />
                <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
                  If they already have a LedgerPro account they'll be added immediately. Otherwise
                  a pending invite is created — it activates automatically when they register.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="input"
                >
                  <option value="member">Member — view and edit clients</option>
                  <option value="admin">Admin — manage staff and settings</option>
                </select>
              </div>
              <Separator />
              <div className="flex gap-3">
                <Button type="submit" disabled={inviting}>
                  {inviting ? 'Sending...' : 'Send Invite'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowInvite(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Remove / Cancel Confirmation Dialog */}
      <Dialog open={!!confirmRemove} onOpenChange={(open) => { if (!open) setConfirmRemove(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmRemove && isPendingInvite(confirmRemove) ? 'Cancel Invite' : 'Remove Staff Member'}
            </DialogTitle>
          </DialogHeader>
          {confirmRemove && isPendingInvite(confirmRemove) ? (
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
              Cancel the pending invite for <strong className="text-[#000A1E]">{confirmRemove.email}</strong>?
              They won't be able to join the firm using this invite.
            </p>
          ) : (
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
              Remove <strong className="text-[#000A1E]">{confirmRemove?.full_name || confirmRemove?.email}</strong> from
              the firm? They will immediately lose access.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)} disabled={removing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemove} disabled={removing}>
              {removing
                ? 'Removing...'
                : confirmRemove && isPendingInvite(confirmRemove)
                  ? 'Cancel Invite'
                  : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : staff.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">No staff members yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Member</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  {canManage && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((member) => {
                  const isSelf = member.user_id === user?.id
                  const isPending = isPendingInvite(member)
                  return (
                    <TableRow key={member.membership_id} className={isPending ? 'opacity-70' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar size="sm">
                            <AvatarFallback className={`text-xs font-semibold ${isPending ? 'bg-amber-100 text-amber-600' : 'bg-[#0078C8]/10 text-[#0078C8]'}`}>
                              {(member.full_name || member.email || '?')[0].toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            {isPending ? (
                              <p className="text-[13px] text-[rgba(0,10,30,0.75)] italic">{member.email}</p>
                            ) : (
                              <>
                                <p className="text-[13px] font-medium text-[#000A1E]">
                                  {member.full_name || <span className="text-[rgba(0,10,30,0.75)] italic">No name</span>}
                                  {isSelf && <span className="ml-1.5 text-[13px] text-[rgba(0,10,30,0.75)]">(you)</span>}
                                </p>
                                <p className="text-[13px] text-[rgba(0,10,30,0.75)]">{member.email}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {isPending ? (
                          <Badge variant="warning">
                            Invite pending
                          </Badge>
                        ) : (
                          <Badge variant={member.status === 'active' ? 'success' : 'default'}>
                            {member.status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.role === 'owner' || !canManage || isPending ? (
                          <Badge variant={member.role === 'owner' ? 'info' : 'default'}>
                            {member.role}
                          </Badge>
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member, e.target.value)}
                            className="appearance-none pr-6 pl-2 py-1 text-[13px] border border-border rounded-md bg-card text-[#000A1E] focus:outline-none focus:ring-1 focus:ring-[#0078C8]/40 cursor-pointer"
                          >
                            {(['member', 'admin'] as const).map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        )}
                      </TableCell>
                      <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)]">
                        {isPending ? (
                          <span className="text-[#CCCED2]">—</span>
                        ) : member.joined_at ? (
                          new Date(member.joined_at).toLocaleDateString()
                        ) : (
                          <span className="text-[#CCCED2]">—</span>
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          {member.role !== 'owner' && !isSelf && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => setConfirmRemove(member)}
                              className={isPending
                                ? 'text-amber-400 hover:text-amber-600 hover:bg-amber-50'
                                : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                              }
                              title={isPending ? 'Cancel invite' : 'Remove staff member'}
                            >
                              {isPending ? <X className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
